import { calculationLimits, designFastVariables, designRoadClasses } from "store/calculation.config";
import { calculateClothoideParameter } from "utilities/genericRoadUtilities/calculateClothoideParameter";
import { calculateSpeedProfileAddition } from "utilities/genericRoadUtilities/calculateSpeedProfileAddition";
import { calculateSuperelevationRunoffLength } from "utilities/genericRoadUtilities/calculateSuperelevationRunoffLength";

export const calculateClothoide = (input) => {
    
    const errors = {
        TOOLS_SPEEDLIMIT_TOO_LOW: false,
        TOOLS_SPEEDLIMIT_TOO_HIGH: false,
        TOOLS_HORIZONTAL_RADIUS_IS_EQUAL_ZERO: false,
        TOOLS_SUPERELEVATION_TOO_HIGH: false,
    }

    let calculationContext = {
        normalizedInput: {...input},
        wasNormalized:
        {
            designCLass: false,
            speedlimit: false,
            firstRadius: false,
            secondRadius: false,
            maxSuperelevation: false,
        },
        errorsToSet: [],
        errorsToUnset: [],
        input:
        {
            designClass: {value: null, valid: null, severity: null},
            speedlimit: {value: null, valid: null, severity: null},
            firstRadius: {value: null, valid: null, severity: null},
            secondRadius: {value: null, valid: null, severity: null},
            maxSuperelevation: {value: null, valid: null, severity: null}
        },
        derived:
        {
            wheelbase: {value: designFastVariables.wheelbase, valid: null, severity: null},
            relativeVerticalSpeed: {value: designFastVariables.relativeVerticalSpeed, valid: null, severity: null},
            speedAddition: {value: null, valid: null, severity: null},
            speedProfileAddition: {value: null, valid: null, severity: null},
            designTable: {value: null, valid: null, severity: null},
        },
        output:
        {
            superelevationRunoffLength: {value: null, valid: null, severity: null},
            clothoideParameter: {value: null, valid: null, severity: null},
            curveDisplacement: {value: null, valid: null, severity: null},
            drivingTime: {value: null, valid: null, severity: null}
        }
    };

    
    validateValues(calculationContext, errors);
    calculateDerived(calculationContext, errors);
    calculateOutput(calculationContext, errors);

    calculationContext.errorsToSet = Object.keys(errors).filter(key => errors[key]);
    calculationContext.errorsToUnset = Object.keys(errors).filter(key => !errors[key]);

    return (calculationContext);
}

const validateValues = (ctx, errors) => {

    // #region validating design class
    if(ctx.normalizedInput.designClass.trim() !== '')
    {
        let designClassObject = designRoadClasses[ctx.normalizedInput.designClass];
        if(designClassObject) ctx.input.designClass = {value: designClassObject, valid: true, severity: null};
        else ctx.input.designClass = {value: null, valid: false, severity: 'error'};
    }
    // #endregion

    // #region speedlimit
    if(ctx.normalizedInput.speedlimit.trim() !== '')
    {
        let speedlimit = Number(ctx.normalizedInput.speedlimit.trim());

        if(speedlimit % 10 !== 0)
        {
            speedlimit = Math.round(speedlimit/10)*10;
            ctx.wasNormalized.speedlimit = true;
        }

        if(speedlimit < calculationLimits.minSpeed)
        {
            ctx.input.speedlimit = {value: null, valid: false, severity: 'error'};
            errors.TOOLS_SPEEDLIMIT_TOO_LOW = true;
        }
        else if(speedlimit > calculationLimits.maxSpeed)
        {
            ctx.input.speedlimit = {value: null, valid: false, severity: 'error'};
            errors.TOOLS_SPEEDLIMIT_TOO_HIGH = true;
        }
        else
        {
            ctx.input.speedlimit = {value: speedlimit, valid: true, severity: null};
        }
    }
    else
    {
        if(ctx.input.designClass.valid)
        {
            console.log(ctx.input.designClass.value);
            ctx.input.speedlimit = {
                valid: true,
                value: ctx.input.designClass.value.speedlimit,
                severity: null
            }

            ctx.wasNormalized.speedlimit = true;
        }
    }
    // #endregion

    // #region first radius
    if(ctx.normalizedInput.firstRadius.trim() !== '')
    {
        const firstRadius = Number(ctx.normalizedInput.firstRadius.trim());

        if(firstRadius === 0)
        {
            ctx.input.firstRadius = {value: null, valid: false, severity: 'error'};
            errors.TOOLS_HORIZONTAL_RADIUS_IS_EQUAL_ZERO = true;
        }
        else
        {
            ctx.input.firstRadius = {value: firstRadius, valid: true, severity: null};
        }
    }
    // #endregion

    // #region second radius
    if(ctx.normalizedInput.secondRadius.trim() !== '')
    {
        const secondRadius = Number(ctx.normalizedInput.secondRadius.trim());

        if(secondRadius === 0)
        {
            ctx.input.secondRadius = {value: null, valid: false, severity: 'error'};
            errors.TOOLS_HORIZONTAL_RADIUS_IS_EQUAL_ZERO = true;
        }
        else
        {
            ctx.input.secondRadius = {value: secondRadius, valid: true, severity: null};
        }
    }
    // #endregion

    // #region max superelevation
    if(ctx.normalizedInput.maxSuperelevation.trim() !== '')
    {
        let maxSuperelevation = Number(ctx.normalizedInput.maxSuperelevation.trim());
        if(maxSuperelevation < 0)
        {
            maxSuperelevation = Math.abs(maxSuperelevation);
            ctx.normalizedInput.maxSuperelevation = maxSuperelevation;
            ctx.wasNormalized.maxSuperelevation = true;
        }

        if(maxSuperelevation > calculationLimits.maxCrossSlope)
        {
            ctx.input.maxSuperelevation = {value: null, valid: false, severity: 'error'};
            errors.TOOLS_SUPERELEVATION_TOO_HIGH = true;
        }
        else
        {
            ctx.input.maxSuperelevation = {value: maxSuperelevation, valid: true, severity: null};
        }
    }
    // #endregion
}

const calculateDerived = (ctx, errors) => {

    if(ctx.input.designClass.valid)
    {
        ctx.derived.speedAddition = {
            value: ctx.input.designClass.value.speedAddition,
            valid: true,
            severity: null
        };
    }
    if(ctx.input.designClass.valid && ctx.input.firstRadius.valid)
    {
        ctx.derived.speedProfileAddition = {
            value: calculateSpeedProfileAddition(
                ctx.input.designClass.value,
                ctx.input.firstRadius.value
            ),
            valid: true,
            severity: null
        };
    }
}

const calculateOutput = (ctx, errors) => {
    
    if(ctx.input.speedlimit.valid && ctx.input.designClass.valid && ctx.input.maxSuperelevation.valid)
    {
        ctx.output.superelevationRunoffLength = {
            value: calculateSuperelevationRunoffLength(
                ctx.input.speedlimit.value + ctx.derived.speedAddition.value + ctx.derived.speedProfileAddition.value,
                ctx.input.maxSuperelevation.value,
                ctx.derived.wheelbase.value,
                ctx.derived.relativeVerticalSpeed.value
            ),
            valid: true,
            serverity: null
        }

        if(ctx.input.firstRadius.valid)
        {
            ctx.output.clothoideParameter = {
                value: calculateClothoideParameter({
                    firstRadius: ctx.input.firstRadius.value,
                    secondRadius: ctx.input.secondRadius.value,
                    superelevationRunoffLength: ctx.output.superelevationRunoffLength.value
                }),
                valid: true,
                serverity: null,
            }

            ctx.output.curveDisplacement = {
                value: ctx.output.clothoideParameter.value ** 4 / (24 * ctx.input.firstRadius.value**3),
                valid: true,
                type: null
            };

            ctx.output.drivingTime = {
                value: 3.6 * ctx.output.superelevationRunoffLength.value/(ctx.input.speedlimit.value + ctx.derived.speedAddition.value + ctx.derived.speedProfileAddition.value),
                valid: true,
                type: null
            };
        }
    }   
}
